import { useEffect } from "react";
import { chunkForTtsSegments, ttsChunkMax } from "../lib/tts.js";
import type { useTts } from "./useTts.js";

/**
 * Read-along highlighting. As narration plays, highlight the sentence(s) being
 * spoken in the editor and keep them in view. Uses the CSS Custom Highlight API
 * so the contentEditable DOM is never mutated (no stray <mark> in the prose).
 *
 * The editor text is rebuilt from the live text nodes (so offsets map exactly to
 * DOM positions) and chunked with the same rule as playback, then the currently
 * sounding chunk's character span is turned into a Range and highlighted.
 */
const HIGHLIGHT_NAME = "tts-reading";

interface TextSpan {
  node: Text | null; // null = a paragraph-break gap that maps to no node
  start: number;
  end: number;
}

function nearestBlock(node: Node, root: HTMLElement): Element | null {
  let el: Node | null = node.parentNode;
  while (el && el !== root) {
    if (el instanceof HTMLElement) {
      const d = getComputedStyle(el).display;
      if (d === "block" || d === "list-item" || d === "flex" || d === "grid") return el;
    }
    el = el.parentNode;
  }
  return root;
}

/** Concatenate the editor's text nodes, inserting paragraph breaks, with a span map. */
function buildReadModel(root: HTMLElement): { text: string; spans: TextSpan[] } {
  const spans: TextSpan[] = [];
  let text = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let lastBlock: Element | null = null;
  let node = walker.nextNode();
  while (node) {
    const tn = node as Text;
    const content = tn.textContent ?? "";
    if (content.length > 0) {
      const block = nearestBlock(tn, root);
      if (lastBlock && block !== lastBlock && text.length > 0) {
        const sepStart = text.length;
        text += "\n\n";
        spans.push({ node: null, start: sepStart, end: text.length });
      }
      lastBlock = block;
      const start = text.length;
      text += content;
      spans.push({ node: tn, start, end: text.length });
    }
    node = walker.nextNode();
  }
  return { text, spans };
}

/** Turn a [start,end) span in the model text into a DOM Range (skipping gaps). */
function rangeFromSpan(spans: TextSpan[], start: number, end: number): Range | null {
  let startNode: Text | null = null;
  let startOffset = 0;
  let endNode: Text | null = null;
  let endOffset = 0;
  for (const span of spans) {
    if (!span.node) continue;
    if (startNode === null && end > span.start && start < span.end) {
      startNode = span.node;
      startOffset = Math.max(0, start - span.start);
    }
    if (start < span.end && end > span.start) {
      endNode = span.node;
      endOffset = Math.min(span.node.length, end - span.start);
    }
  }
  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, Math.min(startOffset, startNode.length));
    range.setEnd(endNode, Math.min(endOffset, endNode.length));
    return range;
  } catch {
    return null;
  }
}

type HighlightRegistry = Map<string, { clear: () => void; add: (r: Range) => void }>;

function highlightsApi(): HighlightRegistry | null {
  const api = (CSS as unknown as { highlights?: HighlightRegistry }).highlights;
  return api ?? null;
}

function setHighlight(range: Range | null): void {
  const api = highlightsApi();
  if (!api) return;
  const Ctor = (window as unknown as { Highlight?: new (...r: Range[]) => { clear: () => void; add: (r: Range) => void } }).Highlight;
  if (!Ctor) return;
  if (!range) {
    api.delete?.(HIGHLIGHT_NAME);
    return;
  }
  const hl = new Ctor(range);
  api.set(HIGHLIGHT_NAME, hl);
}

function scrollIntoView(root: HTMLElement, range: Range): void {
  const scroller = root.closest(".editor-scroll") as HTMLElement | null;
  if (!scroller) return;
  const rect = range.getBoundingClientRect();
  const box = scroller.getBoundingClientRect();
  if (rect.height === 0) return;
  // Only scroll when the highlight is outside the comfortable middle band.
  if (rect.top < box.top + box.height * 0.2 || rect.bottom > box.bottom - box.height * 0.2) {
    scroller.scrollBy({ top: rect.top - (box.top + box.height * 0.35), behavior: "smooth" });
  }
}

export function useReadAlong(
  editorRef: React.RefObject<HTMLElement | null>,
  tts: ReturnType<typeof useTts>
): void {
  const nowPlaying = tts.nowPlaying;
  const serverAvailable = !!tts.status?.available;
  const state = tts.state;

  useEffect(() => {
    const root = editorRef.current;
    if (!highlightsApi() || !root) return;
    if (state === "idle" || nowPlaying < 0) {
      setHighlight(null);
      return;
    }
    const { text, spans } = buildReadModel(root);
    const segs = chunkForTtsSegments(text, ttsChunkMax(serverAvailable));
    const seg = segs[nowPlaying];
    if (!seg) {
      setHighlight(null);
      return;
    }
    const range = rangeFromSpan(spans, seg.start, seg.end);
    setHighlight(range);
    if (range) scrollIntoView(root, range);
  }, [editorRef, nowPlaying, serverAvailable, state]);

  // Clear the highlight when the editor unmounts.
  useEffect(() => () => setHighlight(null), []);
}
